'use client';

import React, { useState, createContext, useContext, useMemo, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

// =============================================================================
// SHELL CONTEXT
// =============================================================================

interface ShellContextValue {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const ShellContext = createContext<ShellContextValue | undefined>(undefined);

export function useShell() {
  const context = useContext(ShellContext);
  if (!context) {
    throw new Error('useShell must be used within an AppShell');
  }
  return context;
}

// =============================================================================
// APP SHELL COMPONENT
// =============================================================================

export interface AppShellProps {
  children: React.ReactNode;
  className?: string;
}

export function AppShell({ children, className }: AppShellProps) {
  const pathname = usePathname();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Memoize context value to prevent unnecessary re-renders
  const shellContextValue = useMemo(() => ({
    sidebarCollapsed,
    setSidebarCollapsed,
  }), [sidebarCollapsed]);

  return (
    <ShellContext.Provider value={shellContextValue}>
      <div className={cn('flex h-screen bg-[var(--surface-bg)] overflow-hidden', className)}>
        {/* Sidebar */}
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top Bar */}
          <TopBar />

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </ShellContext.Provider>
  );
}
